import HttpService from '@/common/services/HttpService'
import {
  type Interaction,
  type InteractionUpdateDTOV3,
  type InteractionCustomDTOV3,
  type InteractionCustom,
  type InteractionCustomCreateOrUpdate,
  type InteractionDTOV3,
  type InteractionStatusDTOV3,
  type InteractionDetailsDTOV1,
  type InteractionDTOV4,
} from '@/common/types/interaction'

import { PrefixTranslationsEnum } from '@/common/types/i18n'
import { includeTranslationsInItems } from '@/common/helpers/services'
import { type InteractionTypeDTOV1, type InteractionFieldConfig } from '@/common/types/interactionProperties'

const ServiceV3CustomTypesPath = 'api/v3/interactions/custom-types'

export const getInteractionTypes = async (): Promise<InteractionTypeDTOV1[]> => {
  return (await HttpService.getData<InteractionTypeDTOV1[]>('/api/v1/interactions/types')) || []
}

export const getCustomInteractionTypes = async (): Promise<InteractionCustomDTOV3[]> => {
  return (await HttpService.getData<InteractionCustomDTOV3[]>(ServiceV3CustomTypesPath)) || []
}

export const getInteractionsV4 = async (caseId?: number, ids?: number[], page?: number, size?: number): Promise<InteractionDTOV4[]> => {
  const params: { caseId?: number; ids?: string; page?: number; size?: number } = {}
  if (caseId) {
    params.caseId = caseId
  }
  if (ids) {
    params.ids = ids.join()
  }
  if (page) {
    params.page = page
  }
  if (size) {
    params.size = size
  }
  return HttpService.getData<InteractionDTOV4[]>(`/api/v4/interactions`, {
    params,
  })
}

export const getBulkInteractions = async (ids?: number[], { includes, expands }: InteractionFieldConfig = {}): Promise<InteractionDetailsDTOV1[]> => {
  const params = {
    include: includes?.size ? [...includes].join() : undefined,
    expand: expands?.size ? [...expands].join() : undefined,
  }
  return HttpService.postData<InteractionDetailsDTOV1[]>(`/api/v1/bulk-interactions/fetch`, { ids }, { params })
}

export const createInteraction = async (interaction: InteractionUpdateDTOV3) => {
  return HttpService.post<InteractionDTOV3>('api/v3/interactions/', interaction)
}

export const getInteraction = async (id: number) => {
  return HttpService.getData<InteractionDTOV3>(`api/v3/interactions/${id}`)
}

export const updateInteractionSensitiveContent = async (id: number, content: string) => {
  return HttpService.post<InteractionDTOV3>(`/api/v3/interactions/${id}/process-confidential`, {
    content,
  })
}

export const updateInteraction = async (interactionId: number, interactionUpdated: InteractionUpdateDTOV3) => {
  return HttpService.put(`api/v3/interactions/${interactionId}`, interactionUpdated)
}

export const updateInteractionStatus = async (interactionId: number, status: InteractionStatusDTOV3) => {
  return HttpService.patch(`api/v3/interactions/${interactionId}`, status)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isInteraction(entity: any): entity is Interaction {
  return (entity as Interaction).interactionDate !== undefined
}

export const getAllInteractionsCustomTypes = async (): Promise<InteractionCustom[]> => {
  return includeTranslationsInItems(
    () =>
      HttpService.getData<InteractionCustomDTOV3[]>(ServiceV3CustomTypesPath, {
        params: {
          fetchAll: true,
        },
      }),
    PrefixTranslationsEnum.INTERACTION,
    'id',
  )
}

export const getInteractionCustomType = (id: UUID) => {
  return HttpService.getData<InteractionCustomDTOV3>(`${ServiceV3CustomTypesPath}/${id}`)
}

export const createInteractionCustomType = async (interaction: InteractionCustomCreateOrUpdate) => {
  return HttpService.post(ServiceV3CustomTypesPath, interaction)
}

export const updateInteractionCustomType = async (id: UUID, interaction: InteractionCustomCreateOrUpdate) => {
  return HttpService.put(`${ServiceV3CustomTypesPath}/${id}`, interaction)
}

export const handleInteractionCustomActivation = (interactionCustom: InteractionCustomDTOV3, active: boolean) => {
  return HttpService.put(`${ServiceV3CustomTypesPath}/${interactionCustom.id}`, {
    ...interactionCustom,
    active,
  })
}
